//React
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
//Libraries
import cx from "classnames";
//Components
import {
  Card,
  DocsCodeBlock,
  DocsFlexSectionBlock,
  DocsScreen,
  FlexBox,
  removeLoader,
  showLoader,
  TitleBlock,
  Image,
} from "components";
//Constants
import { docsConstants } from "../../../constants";
//Utils
import { EventManager, EventNames, useIsInViewport } from "utils";
//Styles
import "./styles.scss";
//Assets
import AndroidIcon from "assets/icons/android.svg";

const DocsHome = () => {
  /* Constants */
  //States
  const [scrolledToElement, setScrolledToElement]: any = useState({
    selectedTab: docsConstants.TITLES.OVERVIEW,
  });

  //Params
  const sdkType: any = useParams();

  //Refs
  const WelcomeSectionRef: any = useRef();
  const isWelcomeSectionInViewport = useIsInViewport(WelcomeSectionRef);
  //Android Sdk
  const InstallationRef: any = useRef();
  const isInstallationInViewport = useIsInViewport(InstallationRef);
  const UsageRef: any = useRef();
  const isUsageInViewport = useIsInViewport(UsageRef);

  //Jsons
  const scrollAccToSpecificElementMap = {
    [docsConstants.TITLES.OVERVIEW]: WelcomeSectionRef,
    [docsConstants.TITLES.ANDROID.INSTALLATION]: InstallationRef,
    [docsConstants.TITLES.ANDROID.USAGE]: UsageRef,
  };

  /* useEffects */
  useEffect(() => {
    if (isWelcomeSectionInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.OVERVIEW,
      });
    } else if (isInstallationInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.ANDROID.INSTALLATION,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    } else if (isUsageInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.ANDROID.USAGE,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    }
  }, [
    isWelcomeSectionInViewport,
    //android sdk
    isInstallationInViewport,
    isUsageInViewport,
  ]);

  /* Functions */
  //Scroll To Specific element
  const scrollToSpecificElement = (selectedTab) => {
    scrollAccToSpecificElementMap[selectedTab]?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  };

  //Render UI
  const WelcomeSection = (
    <div ref={WelcomeSectionRef}>
      <TitleBlock
        title={
          <FlexBox alignCenter className="section-header">
            <Image src={AndroidIcon} className="mr-r-10" />
            <p>Android SDK</p>
          </FlexBox>
        }
        content={
          <p className="fs-16">
            The Android SDK help you get up and running with OTPless faster by
            avoiding the struggles associated with to handle frontend functions.
          </p>
        }
        titleClass="fs-32 font-heavy"
        className="animated fadeInUp mr-b-60"
      />
    </div>
  );

  const AndroidSdk = () => {
    const Installation = (
      <div ref={InstallationRef}>
        <TitleBlock
          title={docsConstants.TITLES.ANDROID.INSTALLATION}
          content={
            <div className="api">
              <DocsFlexSectionBlock
                leftSection={
                  <ul className="fs-16 dots">
                    <li>Minimum SDK Version is api 17</li>
                    <li>
                      <p>
                        Add OTPless aar file in your android project under
                        app/libs folder
                      </p>
                      <a
                        href={docsConstants.ENDPOINT.ANDROID.SDK_LINK}
                        download="OTPless-Android-SDK.aar"
                        className="theme-btn dsp-b w-fc clicking-animation mr-t-15 fs-14 text-dec-none br-9 bg-dark-theme font-heavy pd-10"
                        onClick={() => {
                          EventManager.send(
                            EventNames.EVENTS_NAME.DOCS.ANDROID.DOWNLOAD
                          );
                        }}
                      >
                        Download SDK
                      </a>
                    </li>
                    <li>Add dependencies in app/build.gradle file.</li>
                  </ul>
                }
                rightSection={
                  <DocsCodeBlock
                    title={"Installation"}
                    codeToBeCopied={`
  dependencies {
    implementation'androidx.appcompat: appcompat: 1.2.0'
    implementation'com.google.android.material:material:1.3.0'
    implementation'androidx.constraintlayout:constraintlayout:2.0.4'
    testImplementation'junit: junit:4.+'
    androidTestImplementation'androidx.test.ext:junit:1.1.2'
    androidTestImplementation'androidx.test.espresso:espresso-core:3.3.0'
    implementationfiles('libs/OTPless-Android-SDK.aar')
  }
                      `}
                    code={
                      <>
                        <p className="attr-name mr-v-5">
                          dependencies
                          <span className="tag mr-h-5">&#123;</span>
                        </p>
                        <p className="mr-l-20">
                          <span className="attr-name">implementation</span>
                          <span className="attr-value mr-h-5">
                            'androidx.appcompat: appcompat: 1.2.0'
                          </span>
                        </p>
                        <p className="mr-l-20 mr-t-5">
                          <span className="attr-name">implementation</span>
                          <span className="attr-value mr-h-5">
                            'com.google.android.material:material:1.3.0'
                          </span>
                        </p>
                        <p className="mr-l-20 mr-t-5">
                          <span className="attr-name">implementation</span>
                          <span className="attr-value mr-h-5">
                            'androidx.constraintlayout:constraintlayout:2.0.4'
                          </span>
                        </p>
                        <p className="mr-l-20 mr-t-5">
                          <span className="attr-name">testImplementation</span>
                          <span className="attr-value mr-h-5">
                            'junit: junit:4.+'
                          </span>
                        </p>
                        <p className="mr-l-20 mr-t-5">
                          <span className="attr-name">
                            androidTestImplementation
                          </span>
                          <span className="attr-value mr-h-5">
                            'androidx.test.ext:junit:1.1.2'
                          </span>
                        </p>
                        <p className="mr-l-20 mr-t-5">
                          <span className="attr-name">
                            androidTestImplementation
                          </span>
                          <span className="attr-value mr-h-5">
                            'androidx.test.espresso:espresso-core:3.3.0'
                          </span>
                        </p>
                        <p className="mr-l-20 mr-t-5">
                          <span className="attr-name">implementation</span>
                          <span className="attr-value mr-h-5">
                            files('libs/OTPless-Android-SDK.aar')
                          </span>
                        </p>
                        <p className="tag">&#125;</p>
                      </>
                    }
                  />
                }
              />
            </div>
          }
          titleClass="fs-24 font-heavy mr-t-20"
        />
      </div>
    );

    const Usage = (
      <div ref={UsageRef}>
        <TitleBlock
          title={docsConstants.TITLES.ANDROID.USAGE}
          content={
            <div className="api">
              <DocsFlexSectionBlock
                leftSection={
                  <div className="fs-16">
                    <p>In your Activity file import the following.</p>
                  </div>
                }
                rightSection={
                  <DocsCodeBlock
                    title={"Import Code"}
                    codeToBeCopied={`
  import com.otpless.main.Otpless;
  import com.otpless.main.OtplessIntentRequest;
  import com.otpless.main.OtplessProvider;
  import com.otpless.main.OtplessTokenData;
                      `}
                    code={
                      <>
                        <p className="attr-value">
                          <span className="attr-name">import</span>{" "}
                          com.otpless.main.Otpless;
                        </p>
                        <p className="attr-value mr-v-5">
                          <span className="attr-name">import</span>{" "}
                          com.otpless.main.OtplessIntentRequest;
                        </p>
                        <p className="attr-value mr-b-5">
                          <span className="attr-name">import</span>{" "}
                          com.otpless.main.OtplessProvider;
                        </p>
                        <p className="attr-value">
                          <span className="attr-name">import</span>{" "}
                          com.otpless.main.OtplessTokenData;
                        </p>
                      </>
                    }
                  />
                }
              />
              <DocsFlexSectionBlock
                leftSection={
                  <div className="fs-16">
                    <p>
                      After getting the intentUri via initiate auth from your
                      backend api that connects to otpless service.
                    </p>
                  </div>
                }
                rightSection={
                  <>
                    <DocsCodeBlock
                      title={"onCreate, onOtplessResult"}
                      codeToBeCopied={`
  // declare otpless variable
  private Otpless otpless;

  @Override
  protected void onCreate(Bundle savedInstanceState) {

    super.onCreate(savedInstanceState);

    //Initialize OTPLess Instance
    otpless = OtplessProvider.getInstance(this).init(this::onOtplessResult);

  }

  //Call back function Where token is received

  private void onOtplessResult(@Nullable OtplessTokenData response) {

    if(response == null) return;

    //Send this token to your backend end api to fetch user details from otpless service
    String token=response.getToken();

  }
                      `}
                      code={
                        <>
                          <p className="comment">// declare otpless variable</p>
                          <p className="attr-value mr-v-5">
                            <span className="punctuation">private</span>
                            <span className="attr-name mr-h-5">Otpless</span>
                            <span className="attr-value">otpless</span>;
                          </p>
                          <p className="punctuation mr-t-20">@Override</p>
                          <p>
                            <span className="punctuation">protected</span>
                            <span className="attr-name mr-h-5">void</span>
                            <span className="attr-value mr-r-5">onCreate</span>
                            <span className="tag">(</span>
                            <span className="attr-name mr-h-5">Bundle</span>
                            <span className="attr-value mr-r-5">
                              savedInstanceState
                            </span>
                            <span className="tag">)</span>
                            <span className="tag mr-h-5">&#123;</span>
                          </p>
                          <p className="attr-value mr-l-20">
                            <span className="attr-name">super</span>
                            .onCreate<span className="tag">(</span>
                            savedInstanceState<span className="tag">)</span>;
                          </p>
                          <p className="comment mr-t-20 mr-l-20">
                            {" "}
                            //Initialize OTPLess Instance
                          </p>
                          <p className="mr-t-5 mr-l-20">
                            <span className="punctuation">otpless</span>
                            <span className="mr-h-5">=</span>
                            <span className="attr-name">OtplessProvider</span>.
                            <span className="attr-value">getInstance</span>
                            <span className="tag">(</span>
                            <span className="attr-value">this</span>
                            <span className="tag">)</span>.
                            <span className="attr-value">init</span>
                            <span className="tag">(</span>
                            <span className="attr-value">this</span>
                            <span className="tag mr-h-5">::</span>
                            <span className="attr-value">onOtplessResult</span>
                            <span className="tag">)</span>;
                          </p>
                          <p className="tag">&#125;</p>
                          {/* Second Function */}
                          <p className="comment mr-t-40">
                            //Call back function Where token is received
                          </p>
                          <p>
                            <span className="punctuation">private</span>
                            <span className="attr-name mr-h-5">void</span>
                            <span className="attr-value mr-r-5">
                              onOtplessResult
                            </span>
                            <span className="tag">(</span>
                            <span className="punctuation mr-l-5">
                              @Nullable
                            </span>
                            <span className="attr-name mr-h-5">
                              OtplessTokenData
                            </span>
                            <span className="attr-value mr-r-5">response</span>
                            <span className="tag">)</span>
                            <span className="tag mr-h-5">&#123;</span>
                          </p>
                          <p className="mr-l-20">
                            <span className="attr-name">if</span>
                            <span className="tag mr-h-5">(</span>
                            <span className="attr-value">response</span>
                            <span className="tag mr-h-5">==</span>
                            <span className="comment">null</span>
                            <span className="tag mr-h-5">)</span>
                            <span className="attr-name">return</span>;
                          </p>
                          <p className="comment mr-t-20 mr-l-20">
                            //Send this token to your backend end api to fetch
                            user details from otpless service
                          </p>
                          <p className="mr-t-5 mr-l-20">
                            <span className="attr-name">String</span>
                            <span className="attr-value mr-h-5">token</span>
                            <span className="tag mr-h-5">=</span>
                            <span className="attr-name">response</span>.
                            <span className="attr-value">getToken</span>
                            <span className="tag">()</span>;
                          </p>
                          <p className="tag">&#125;</p>
                        </>
                      }
                    />
                    <DocsCodeBlock
                      title={"initiateOtplessFlow"}
                      codeToBeCopied={`
  private void initiateOtplessFlow (String intentUri ) {

    //While you create a request with otpless sdk you can define your own loading text and color

    final OtplessIntentRequest request = new OtplessIntentRequest (intentUri)
    //.setLoadingText("Please wait...")
    .setProgressBarColor(R.color.purple_200);

    otpless.openOtpless(request);

  }
                      `}
                      code={
                        <>
                          <p>
                            <span className="punctuation">private</span>
                            <span className="attr-name mr-h-5">void</span>
                            <span className="attr-value mr-r-5">
                              initiateOtplessFlow
                            </span>
                            <span className="tag">(</span>
                            <span className="attr-name mr-h-5">String</span>
                            <span className="attr-value mr-r-5">intentUri</span>
                            <span className="tag">)</span>
                            <span className="tag mr-h-5">&#123;</span>
                          </p>
                          <p className="comment mr-v-20 mr-l-20">
                            {" "}
                            //While you create a request with otpless sdk you
                            can define your own loading text and color
                          </p>
                          <p className="mr-l-20">
                            <span className="punctuation">final</span>
                            <span className="attr-name mr-h-5">
                              OtplessIntentRequest
                            </span>
                            <span className="attr-value mr-r-5">request</span>
                            <span className="tag"> = </span>
                            <span className="attr-name mr-h-5">new</span>
                            <span className="attr-value mr-r-5">
                              OtplessIntentRequest
                            </span>
                            <span className="tag">(</span>
                            <span className="attr-value mr-h-5">intentUri</span>
                            <span className="tag">)</span>
                          </p>
                          <p className="comment mr-t-5 mr-l-20">
                            {" "}
                            //.setLoadingText("Please wait...")
                          </p>
                          <p className="mr-t-5 mr-l-20">
                            .
                            <span className="attr-value">
                              setProgressBarColor
                            </span>
                            <span className="tag">(</span>
                            <span className="punctuation">R</span>.
                            <span className="attr-name">color</span>.
                            <span className="attr-value">purple_200</span>
                            <span className="tag">)</span>;
                          </p>
                          <p className="mr-t-5 mr-l-20">
                            <span className="attr-name">otpless</span>.
                            <span className="attr-value">openOtpless</span>
                            <span className="tag">(</span>
                            <span className="attr-value">request</span>
                            <span className="tag">)</span>;
                          </p>
                          <p className="tag">&#125;</p>
                        </>
                      }
                      className="mr-t-40"
                    />
                  </>
                }
                className={"mr-t-40"}
              />
            </div>
          }
          titleClass="fs-24 font-heavy mr-t-40 pd-t-40 dashed-border-top"
        />
      </div>
    );

    return (
      <TitleBlock
        title={""}
        content={
          <div className="rest-api-sdk">
            {Installation}
            {Usage}
          </div>
        }
        titleClass="fs-24 font-heavy"
        className="animated fadeInUp delay-01s border-top mr-b-60 pd-t-30"
      />
    );
  };

  //Return UI
  return (
    <DocsScreen
      className="docs-home-screen"
      hasSideMenu
      hasHeader
      screenAnimationClass="no-animation"
      tabSelectCallback={scrollToSpecificElement}
      scrolledToElement={{ ...scrolledToElement }}
    >
      {WelcomeSection}
      {AndroidSdk()}
    </DocsScreen>
  );
};

export default DocsHome;
